/**
 * Espelho local de PlanRules.ts do backend — usado como FALLBACK de exibição
 * quando GET /users/billing-preview falha e, desde out/2026, para o valor
 * mensal ao vivo da tela de cadastro (o backend recalcula no envio). A fonte
 * da verdade do cálculo de mensalidade é o backend. Ao alterar
 * preços/limites, mudar os DOIS arquivos.
 */
export interface PlanRuleInfo {
  name: string;
  price: number;
  includedConsumerUnits: number;
  maxConsumerUnits: number;
  allowsGroupA: boolean;
  allowsBulkUpload: boolean; // upload em massa de faturas (só planos pagos)
  // Conexão com o inversor e geração automática (out/2026: só planos pagos —
  // o Free informa a geração à mão). Espelha PlanRules.allowsMonitoringIntegration.
  allowsMonitoringIntegration: boolean;
  additionalCostGroupB: number;
  additionalCostGroupA: number;
  // Contas multiusuário (set/2026): membros de equipe permitidos (0 = recurso
  // indisponível). Só o Enterprise libera. Espelha PlanRules.maxTeamMembers.
  maxTeamMembers: number;
  tagline: string;
  recommended: boolean;
}

export const PLAN_RULES_LIST: PlanRuleInfo[] = [
  {
    name: 'Free',
    price: 0,
    includedConsumerUnits: 1,
    maxConsumerUnits: 1,
    allowsGroupA: false,
    allowsBulkUpload: false,
    allowsMonitoringIntegration: false,
    additionalCostGroupB: 0,
    additionalCostGroupA: 0,
    maxTeamMembers: 0,
    tagline: 'Para começar sem custo',
    recommended: false,
  },
  {
    name: 'Starter',
    price: 19.9,
    includedConsumerUnits: 1,
    maxConsumerUnits: Infinity,
    allowsGroupA: false,
    allowsBulkUpload: true,
    allowsMonitoringIntegration: true,
    additionalCostGroupB: 9.9,
    additionalCostGroupA: 0,
    maxTeamMembers: 0,
    tagline: 'Para pequenos consumidores',
    recommended: false,
  },
  {
    name: 'Business',
    price: 49.9,
    includedConsumerUnits: 1,
    maxConsumerUnits: Infinity,
    allowsGroupA: true,
    allowsBulkUpload: true,
    allowsMonitoringIntegration: true,
    additionalCostGroupB: 19.9,
    additionalCostGroupA: 49.9,
    maxTeamMembers: 0,
    tagline: 'Para empresas em crescimento',
    recommended: true,
  },
  {
    name: 'Enterprise',
    price: 199.9,
    includedConsumerUnits: 8,
    maxConsumerUnits: Infinity,
    allowsGroupA: true,
    allowsBulkUpload: true,
    allowsMonitoringIntegration: true,
    additionalCostGroupB: 9.9,
    additionalCostGroupA: 29.9,
    maxTeamMembers: Infinity, // sem limite (25/set/2026)
    tagline: 'Para grandes operações',
    recommended: false,
  },
];

export const getPlanRuleInfo = (planName: string): PlanRuleInfo | undefined =>
  PLAN_RULES_LIST.find((p) => p.name === planName);

export const canBulkUpload = (planName: string): boolean =>
  getPlanRuleInfo(planName)?.allowsBulkUpload ?? false;

// Recebimento automático de faturas por e-mail — exclusivo dos planos pagos
// (Starter/Business/Enterprise). Free (price 0) fica de fora e vê o CTA de upgrade.
export const canAutoReceiveInvoice = (planName: string): boolean =>
  (getPlanRuleInfo(planName)?.price ?? 0) > 0;

// Conexão com o inversor (geração automática, alerta de sistema parado) — só
// planos pagos desde out/2026. O gate de verdade é o backend.
export const canIntegrateMonitoring = (planName: string): boolean =>
  getPlanRuleInfo(planName)?.allowsMonitoringIntegration ?? false;

// Conta multiusuário: o plano permite convidar membros de equipe? (só Enterprise)
export const canInviteTeamMembers = (planName: string): boolean =>
  (getPlanRuleInfo(planName)?.maxTeamMembers ?? 0) > 0;

export const getMaxTeamMembers = (planName: string): number =>
  getPlanRuleInfo(planName)?.maxTeamMembers ?? 0;

// Relatório de emissões de escopo 2 (out/2026): só Enterprise. Espelha
// PlanRules.canAccessEmissionsReport — o gate de verdade é o backend.
export const canAccessEmissionsReport = (planName: string): boolean =>
  planName === 'Enterprise';

export interface MonthlyEstimate {
  planPrice: number;
  additionalGroupB: number;
  additionalGroupBCount: number;
  additionalGroupA: number;
  additionalGroupACount: number;
  total: number;
}

/**
 * Mensalidade estimada pelas regras do plano: preço-base + UCs além das
 * incluídas. Os slots incluídos são preenchidos com Grupo A primeiro (o
 * adicional mais caro), depois Grupo B — minimiza a fatura. Espelha
 * `computeMonthlyFee` do backend. Saiu de /conta em out/2026, quando o cadastro
 * com plano virou o segundo uso.
 */
export const computeMonthlyEstimate = (
  plan: PlanRuleInfo,
  groupBCount: number,
  groupACount: number
): MonthlyEstimate => {
  const effectiveGroupA = plan.allowsGroupA ? groupACount : 0;

  const includedForA = Math.min(effectiveGroupA, plan.includedConsumerUnits);
  const remainingIncluded = plan.includedConsumerUnits - includedForA;

  const addGroupACount = Math.max(0, effectiveGroupA - includedForA);
  const addGroupBCount = Math.max(0, groupBCount - remainingIncluded);

  const addGroupBCost = addGroupBCount * plan.additionalCostGroupB;
  const addGroupACost = addGroupACount * plan.additionalCostGroupA;

  return {
    planPrice: plan.price,
    additionalGroupB: addGroupBCost,
    additionalGroupBCount: addGroupBCount,
    additionalGroupA: addGroupACost,
    additionalGroupACount: addGroupACount,
    total: plan.price + addGroupBCost + addGroupACost,
  };
};
